//! Saved-views toolbar + rule-builder dialog for the Catalogue Editor.
//!
//! Coexists with the existing six-input filter row in editor.tsx (the
//! dropdowns operators already know). Provides:
//!   - a dropdown of saved views (filter presets) — pick one to apply.
//!   - a "+ Filter" button that opens the rule-builder.
//!   - the rule-builder dialog itself: name, criteria, save/load/delete.
//!
//! The active expression is reported up via `onExpressionChange`. The
//! editor includes it as the `filterExpr` query param on
//! `/api/v1/catalogue/rows`; the backend evaluator (filter_expr.rs)
//! ANDs it into the existing WHERE clause. The 6 legacy dropdowns
//! still work and AND-combine with the expression.

import { RiAddLine, RiCloseLine, RiDeleteBinLine, RiFilterLine } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

/// Wire shape for `GET /api/v1/catalogue/views`.
export interface CatalogueView {
	id: string;
	name: string;
	criteria: FilterNode;
	visibility: "shared" | "private";
	createdBy: string;
	createdAt: string;
	updatedAt: string;
}

/// Tree node. Leaf = field + comparator + value; Group = op + children.
/// Mirrors the JSON the backend evaluator parses.
export type FilterNode = FilterLeaf | FilterGroup;
export interface FilterLeaf {
	field: string;
	comparator: string;
	value: unknown;
	value2?: unknown;
}
export interface FilterGroup {
	op: "and" | "or";
	children: FilterNode[];
}

function isGroup(node: FilterNode): node is FilterGroup {
	return (node as FilterGroup).op !== undefined;
}

/// Field type → comparator allowlist (mirrors the backend rule).
/// Each entry is `{ key, label, type }`. New filterable fields go
/// here AND in the backend `filter_expr::resolve_field`.
const FIELDS: Array<{ key: string; label: string; type: "string" | "number" | "boolean" }> = [
	// Burresi-native catalogue_row columns
	{ key: "export_enabled", label: "Export enabled", type: "boolean" },
	{ key: "has_missing_mandatory", label: "Has missing mandatory", type: "boolean" },
	{ key: "stock_quantity", label: "Stock quantity", type: "number" },
	{ key: "total_article_stock", label: "Article total stock", type: "number" },
	{ key: "commercial_discount", label: "Commercial discount", type: "number" },
	{ key: "size", label: "Size", type: "string" },
	{ key: "article_number", label: "Article number", type: "string" },
	// Farfetch target columns (string-typed via computed_values JSONB)
	{ key: "brand", label: "Brand", type: "string" },
	{ key: "brand_product_id", label: "Brand Product ID", type: "string" },
	{ key: "partner_product_id", label: "Partner Product ID", type: "string" },
	{ key: "brand_color_id", label: "Brand Color ID", type: "string" },
	{ key: "season", label: "Season", type: "string" },
	{ key: "collection", label: "Collection", type: "string" },
	{ key: "gender", label: "Gender", type: "string" },
	{ key: "category", label: "Category", type: "string" },
	{ key: "size_scale", label: "Size Scale", type: "string" },
	{ key: "local_market_price", label: "Local Market Price", type: "number" },
	{ key: "barcode", label: "Barcode", type: "string" },
	{ key: "image_url", label: "Image URL", type: "string" },
	{ key: "product_name", label: "Product Name", type: "string" },
	{ key: "item_description", label: "Item Description", type: "string" },
	{ key: "additional_details", label: "Additional Details", type: "string" },
	{ key: "size_and_fit_details", label: "Size and Fit Details", type: "string" },
	{ key: "made_in", label: "Made In", type: "string" },
	{ key: "composition", label: "Composition", type: "string" },
];

const COMPARATORS: Record<string, Array<{ value: string; label: string }>> = {
	string: [
		{ value: "equals", label: "equals" },
		{ value: "not_equals", label: "not equals" },
		{ value: "contains", label: "contains" },
		{ value: "starts_with", label: "starts with" },
		{ value: "is_empty", label: "is empty" },
		{ value: "is_not_empty", label: "is not empty" },
	],
	number: [
		{ value: "equals", label: "=" },
		{ value: "not_equals", label: "≠" },
		{ value: "gt", label: ">" },
		{ value: "gte", label: "≥" },
		{ value: "lt", label: "<" },
		{ value: "lte", label: "≤" },
		{ value: "between", label: "between" },
		{ value: "is_null", label: "is empty" },
		{ value: "is_not_null", label: "is not empty" },
	],
	boolean: [
		{ value: "is_true", label: "is true" },
		{ value: "is_false", label: "is false" },
	],
};

/// Does this comparator take a value input? (Empty checks don't.)
function takesValue(comparator: string): boolean {
	return !["is_empty", "is_not_empty", "is_null", "is_not_null", "is_true", "is_false"].includes(
		comparator,
	);
}

/** Comparators that take a second value input (inclusive upper bound). */
function takesTwoValues(comparator: string): boolean {
	return comparator === "between";
}

/**
 * Next leaf state after a comparator change. Built fresh (no spread)
 * so a stale `value2` from a previous `between` is dropped.
 */
export function leafWithComparator(leaf: FilterLeaf, comparator: string): FilterLeaf {
	return { field: leaf.field, comparator, value: "" };
}

interface FilterBarProps {
	/// Active expression — JSON-stringified for the backend, or null
	/// if no rule-builder filter is active. The editor passes this as
	/// `filterExpr` on the rows query.
	expression: string | null;
	onExpressionChange: (next: string | null) => void;
}

export function FilterBar({ expression, onExpressionChange }: FilterBarProps) {
	const queryClient = useQueryClient();
	const { data: viewsData } = $api.useQuery(
		"get",
		"/api/v1/catalogue/views" as never,
		{ params: { query: { target: "farfetch" } } } as never,
	);
	const views = (viewsData as CatalogueView[] | undefined) ?? [];

	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingView, setEditingView] = useState<CatalogueView | null>(null);

	function invalidateViews() {
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/catalogue/views"] });
	}

	function applyView(view: CatalogueView) {
		onExpressionChange(JSON.stringify(view.criteria));
	}

	function clearExpression() {
		onExpressionChange(null);
	}

	function openNew() {
		setEditingView(null);
		setDialogOpen(true);
	}

	function openEdit(view: CatalogueView) {
		setEditingView(view);
		setDialogOpen(true);
	}

	return (
		<div className="flex items-center gap-2 flex-wrap">
			<Select
				value=""
				onValueChange={(id) => {
					if (id === "__new__") {
						openNew();
						return;
					}
					const v = views.find((x) => x.id === id);
					if (v) applyView(v);
				}}
			>
				<SelectTrigger className="w-[200px]">
					<SelectValue placeholder="Saved views..." />
				</SelectTrigger>
				<SelectContent>
					{views.length === 0 ? (
						<div className="px-2 py-2 text-xs text-muted-foreground">No saved views yet</div>
					) : (
						views.map((v) => (
							<SelectItem key={v.id} value={v.id}>
								<div className="flex items-center gap-2">
									<RiFilterLine className="size-3" />
									<span>{v.name}</span>
									{v.visibility === "private" && (
										<span className="text-[10px] text-muted-foreground">(private)</span>
									)}
								</div>
							</SelectItem>
						))
					)}
					<div className="border-t my-1" />
					<SelectItem value="__new__">
						<div className="flex items-center gap-2">
							<RiAddLine className="size-3" />
							<span>New view…</span>
						</div>
					</SelectItem>
				</SelectContent>
			</Select>

			<Button variant="outline" size="sm" onClick={openNew}>
				<RiAddLine className="size-4 mr-1" />
				Filter
			</Button>

			{expression !== null && (
				<div className="flex items-center gap-1 px-2 py-1 rounded-md border bg-primary/5 text-xs">
					<RiFilterLine className="size-3" />
					<span className="font-medium">Custom filter active</span>
					<button
						type="button"
						onClick={clearExpression}
						className="hover:text-destructive ml-1"
						aria-label="Clear filter"
					>
						<RiCloseLine className="size-3" />
					</button>
				</div>
			)}

			{views.length > 0 && (
				<details className="text-xs">
					<summary className="cursor-pointer text-muted-foreground hover:text-foreground">
						Manage views
					</summary>
					<div className="mt-2 space-y-1 absolute z-10 bg-background border rounded-md shadow-md p-2 min-w-[260px]">
						{views.map((v) => (
							<div
								key={v.id}
								className="flex items-center justify-between gap-2 px-2 py-1 hover:bg-muted/50 rounded"
							>
								<button
									type="button"
									onClick={() => openEdit(v)}
									className="text-left flex-1 text-xs"
								>
									{v.name}
									{v.visibility === "private" && (
										<span className="ml-1 text-muted-foreground">(private)</span>
									)}
								</button>
							</div>
						))}
					</div>
				</details>
			)}

			{dialogOpen && (
				<RuleBuilderDialog
					existing={editingView}
					onClose={() => {
						setDialogOpen(false);
						setEditingView(null);
					}}
					onSaved={(saved) => {
						setDialogOpen(false);
						setEditingView(null);
						invalidateViews();
						applyView(saved);
					}}
					onDeleted={() => {
						setDialogOpen(false);
						setEditingView(null);
						invalidateViews();
						clearExpression();
					}}
					onApply={(expr) => {
						setDialogOpen(false);
						setEditingView(null);
						onExpressionChange(JSON.stringify(expr));
					}}
				/>
			)}
		</div>
	);
}

interface RuleBuilderDialogProps {
	existing: CatalogueView | null;
	onClose: () => void;
	onSaved: (view: CatalogueView) => void;
	onDeleted: () => void;
	onApply: (expr: FilterNode) => void;
}

function RuleBuilderDialog({
	existing,
	onClose,
	onSaved,
	onDeleted,
	onApply,
}: RuleBuilderDialogProps) {
	const [name, setName] = useState(existing?.name ?? "");
	const [visibility, setVisibility] = useState<"shared" | "private">(
		existing?.visibility ?? "shared",
	);
	const [root, setRoot] = useState<FilterGroup>(
		existing && isGroup(existing.criteria) ? existing.criteria : { op: "and", children: [] },
	);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (existing) {
			setName(existing.name);
			setVisibility(existing.visibility);
			setRoot(isGroup(existing.criteria) ? existing.criteria : { op: "and", children: [] });
		}
	}, [existing]);

	async function handleApplyOnly() {
		onApply(root);
	}

	async function handleSave() {
		if (!name.trim()) {
			toast.error("Name is required");
			return;
		}
		setSaving(true);
		try {
			if (existing) {
				const { data, error } = await fetchClient.PUT(
					"/api/v1/catalogue/views/{id}" as never,
					{
						params: { path: { id: existing.id } },
						body: { name: name.trim(), criteria: root, visibility },
					} as never,
				);
				if (error) {
					toast.error("Failed to save view");
					return;
				}
				toast.success("View updated");
				onSaved(data as CatalogueView);
			} else {
				const { data, error } = await fetchClient.POST(
					"/api/v1/catalogue/views" as never,
					{
						params: { query: { target: "farfetch" } },
						body: { name: name.trim(), criteria: root, visibility },
					} as never,
				);
				if (error) {
					toast.error("Failed to create view");
					return;
				}
				toast.success("View created");
				onSaved(data as CatalogueView);
			}
		} finally {
			setSaving(false);
		}
	}

	async function handleDelete() {
		if (!existing) return;
		if (!confirm(`Delete view "${existing.name}"?`)) return;
		const { error } = await fetchClient.DELETE(
			"/api/v1/catalogue/views/{id}" as never,
			{ params: { path: { id: existing.id } } } as never,
		);
		if (error) {
			toast.error("Failed to delete view");
			return;
		}
		toast.success("View deleted");
		onDeleted();
	}

	return (
		<Dialog open onOpenChange={(o) => !o && onClose()}>
			<DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle>{existing ? "Edit view" : "New filter"}</DialogTitle>
					<DialogDescription>
						Build a filter expression. Save it as a view to reuse later.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					<div>
						<Label htmlFor="view-name">View name</Label>
						<Input
							id="view-name"
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder="e.g. Burberry, FS2025, ready to export"
						/>
					</div>

					<div className="flex items-center gap-3">
						<Label htmlFor="view-visibility" className="cursor-pointer">
							Shared with org
						</Label>
						<Switch
							id="view-visibility"
							checked={visibility === "shared"}
							onCheckedChange={(v) => setVisibility(v ? "shared" : "private")}
						/>
						<span className="text-xs text-muted-foreground">
							{visibility === "shared"
								? "Everyone in the org sees + uses this view"
								: "Only you see this view"}
						</span>
					</div>

					<div>
						<Label>Criteria</Label>
						<GroupEditor group={root} onChange={setRoot} />
					</div>
				</div>

				<DialogFooter className="gap-2">
					{existing && (
						<Button variant="destructive" onClick={handleDelete} className="mr-auto">
							<RiDeleteBinLine className="size-4 mr-1" />
							Delete
						</Button>
					)}
					<Button variant="outline" onClick={onClose}>
						Cancel
					</Button>
					<Button variant="outline" onClick={handleApplyOnly}>
						Apply without saving
					</Button>
					<Button onClick={handleSave} disabled={saving}>
						{saving ? "Saving..." : existing ? "Save changes" : "Save view"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

interface GroupEditorProps {
	group: FilterGroup;
	onChange: (next: FilterGroup) => void;
}

function GroupEditor({ group, onChange }: GroupEditorProps) {
	function updateChild(idx: number, child: FilterNode) {
		onChange({ ...group, children: group.children.map((c, i) => (i === idx ? child : c)) });
	}
	function removeChild(idx: number) {
		onChange({ ...group, children: group.children.filter((_, i) => i !== idx) });
	}
	function addLeaf() {
		const first = FIELDS[0];
		if (!first) return;
		const leaf: FilterLeaf = {
			field: first.key,
			comparator: COMPARATORS[first.type]?.[0]?.value ?? "equals",
			value: "",
		};
		onChange({ ...group, children: [...group.children, leaf] });
	}
	function addGroup() {
		onChange({
			...group,
			children: [...group.children, { op: "and", children: [] } as FilterGroup],
		});
	}

	return (
		<div className="rounded-md border p-3 space-y-2 bg-muted/20">
			<div className="flex items-center gap-2 text-xs">
				<span className="text-muted-foreground">Match</span>
				<Select
					value={group.op}
					onValueChange={(v) => onChange({ ...group, op: v as "and" | "or" })}
				>
					<SelectTrigger className="w-[80px] h-7">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="and">all</SelectItem>
						<SelectItem value="or">any</SelectItem>
					</SelectContent>
				</Select>
				<span className="text-muted-foreground">of the following</span>
			</div>

			{group.children.length === 0 ? (
				<p className="text-xs text-muted-foreground italic px-1 py-2">
					No criteria yet. Add one below.
				</p>
			) : (
				<div className="space-y-2">
					{group.children.map((child, idx) => (
						<div
							key={`${idx}-${isGroup(child) ? "g" : child.field}`}
							className="flex items-start gap-2"
						>
							<div className="flex-1">
								{isGroup(child) ? (
									<GroupEditor group={child} onChange={(g) => updateChild(idx, g)} />
								) : (
									<LeafEditor leaf={child} onChange={(l) => updateChild(idx, l)} />
								)}
							</div>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => removeChild(idx)}
								aria-label="Remove criterion"
								className="size-7 p-0"
							>
								<RiCloseLine className="size-4" />
							</Button>
						</div>
					))}
				</div>
			)}

			<div className="flex gap-2 pt-1">
				<Button variant="outline" size="sm" onClick={addLeaf}>
					<RiAddLine className="size-3 mr-1" />
					Add criterion
				</Button>
				<Button variant="outline" size="sm" onClick={addGroup}>
					<RiAddLine className="size-3 mr-1" />
					Add group
				</Button>
			</div>
		</div>
	);
}

interface LeafEditorProps {
	leaf: FilterLeaf;
	onChange: (next: FilterLeaf) => void;
}

export function LeafEditor({ leaf, onChange }: LeafEditorProps) {
	const fieldMeta = FIELDS.find((f) => f.key === leaf.field) ?? FIELDS[0];
	const comparators = COMPARATORS[fieldMeta?.type ?? "string"] ?? [];
	const showValue = takesValue(leaf.comparator);
	const showValue2 = takesTwoValues(leaf.comparator);

	return (
		<div className="flex items-center gap-1.5 flex-wrap">
			<Select
				value={leaf.field}
				onValueChange={(field) => {
					const newMeta = FIELDS.find((f) => f.key === field) ?? FIELDS[0];
					const newComparator = COMPARATORS[newMeta?.type ?? "string"]?.[0]?.value ?? "equals";
					onChange({ field, comparator: newComparator, value: "" });
				}}
			>
				<SelectTrigger className="w-[180px] h-8">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{FIELDS.map((f) => (
						<SelectItem key={f.key} value={f.key}>
							{f.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<Select
				value={leaf.comparator}
				onValueChange={(comparator) => onChange(leafWithComparator(leaf, comparator))}
			>
				<SelectTrigger className="w-[130px] h-8">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					{comparators.map((c) => (
						<SelectItem key={c.value} value={c.value}>
							{c.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{showValue && (
				<Input
					value={(leaf.value ?? "") as string | number}
					onChange={(e) => onChange({ ...leaf, value: e.target.value })}
					placeholder={showValue2 ? "min" : fieldMeta?.type === "number" ? "0" : "value"}
					className="h-8 flex-1 min-w-[100px]"
					type={fieldMeta?.type === "number" ? "number" : "text"}
					aria-label={showValue2 ? "Lower bound" : undefined}
				/>
			)}
			{showValue2 && (
				<>
					<span className="text-xs text-muted-foreground">and</span>
					<Input
						value={(leaf.value2 ?? "") as string | number}
						onChange={(e) => onChange({ ...leaf, value2: e.target.value })}
						placeholder="max"
						className="h-8 flex-1 min-w-[100px]"
						type={fieldMeta?.type === "number" ? "number" : "text"}
						aria-label="Upper bound"
					/>
				</>
			)}
		</div>
	);
}
